import { Button, StatusBadge } from '@ai-tutor/ui';
import { CreditCard, Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  useCreateSetupIntent,
  useDeletePaymentMethod,
  usePaymentMethods,
  useSetDefaultPaymentMethod,
} from '@/hooks/account/useBilling';
import type { PaymentMethod } from '@/lib/api-client';
import { notify } from '@/lib/notify';

import { BillingReadError } from '@/components/features/billing/cards/BillingReadError';
import { AddPaymentMethodForm } from '@/components/features/billing/forms/AddPaymentMethodForm';
import { StripeProvider } from '@/components/features/billing/StripeProvider';

/**
 * Shows saved cards for the org's Stripe customer, with set-default and
 * delete actions. When the user clicks "Add payment method" we fetch a
 * SetupIntent client_secret, mount ``<StripeProvider>`` with it, and
 * render ``<AddPaymentMethodForm>`` inline.
 *
 * The list is hidden entirely if the org has no Stripe customer yet
 * (free tier with no prior purchase) - parents should render an empty
 * state above instead.
 */
export function PaymentMethodList() {
  const pmsQuery = usePaymentMethods();
  const setDefault = useSetDefaultPaymentMethod();
  const deletePm = useDeletePaymentMethod();
  const setupIntent = useCreateSetupIntent();

  const [adding, setAdding] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const startAdding = async () => {
    setAdding(true);
    try {
      const r = await setupIntent.mutateAsync();
      setClientSecret(r.client_secret);
    } catch (err) {
      notify.error(err, "Couldn't start adding a card");
      setAdding(false);
    }
  };

  const closeAdd = () => {
    setAdding(false);
    setClientSecret(null);
  };

  const onAdded = () => {
    closeAdd();
    pmsQuery.refetch();
  };

  if (pmsQuery.isLoading) {
    return (
      <div className='rounded-lg border border-border p-6 text-center'>
        <Loader2 className='mx-auto h-4 w-4 animate-spin text-muted-foreground' />
      </div>
    );
  }

  const methods = pmsQuery.data ?? [];

  return (
    <div className='space-y-3'>
      {/* Checked before the empty branch: a failed read has no data, and
          falling through told the user they had no card on file. */}
      {pmsQuery.isError && (
        <BillingReadError>
          Couldn&apos;t load your saved cards. Refresh the page to try again.
        </BillingReadError>
      )}

      {!pmsQuery.isError && methods.length === 0 && !adding && (
        <div className='rounded-lg border border-dashed border-border p-6 text-center'>
          <p className='text-sm text-muted-foreground'>
            No payment methods on file yet.
          </p>
        </div>
      )}

      {methods.map(pm => (
        <PaymentMethodRow
          key={pm.id}
          pm={pm}
          onSetDefault={() =>
            setDefault.mutate(pm.id, {
              onSuccess: () => notify.success('Default card updated'),
              onError: err => notify.error(err, "Couldn't update default card"),
            })
          }
          onDelete={() =>
            deletePm.mutate(pm.id, {
              onSuccess: () => notify.success('Card removed'),
              onError: err => notify.error(err, "Couldn't remove card"),
            })
          }
          settingDefault={
            setDefault.isPending && setDefault.variables === pm.id
          }
          deleting={deletePm.isPending && deletePm.variables === pm.id}
        />
      ))}

      {adding && clientSecret ? (
        <div
          data-testid='add-payment-method-panel'
          className='rounded-lg border border-border p-5'
        >
          <h4 className='mb-3 text-sm font-medium text-foreground'>
            Add a card
          </h4>
          <StripeProvider clientSecret={clientSecret}>
            <AddPaymentMethodForm
              returnUrl={window.location.href}
              onSuccess={onAdded}
              onCancel={closeAdd}
            />
          </StripeProvider>
        </div>
      ) : (
        <Button
          data-testid='add-payment-method-button'
          size='sm'
          className='max-sm:h-11'
          onClick={startAdding}
          disabled={adding && !clientSecret}
        >
          {adding && !clientSecret ? (
            <>
              <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
              Loading…
            </>
          ) : (
            <>
              <Plus className='mr-1 h-3.5 w-3.5' />
              Add payment method
            </>
          )}
        </Button>
      )}
    </div>
  );
}

function PaymentMethodRow({
  pm,
  onSetDefault,
  onDelete,
  settingDefault,
  deleting,
}: {
  pm: PaymentMethod;
  onSetDefault: () => void;
  onDelete: () => void;
  settingDefault: boolean;
  deleting: boolean;
}) {
  const brandLabel = pm.brand
    ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
    : 'Card';
  const expiry =
    pm.exp_month && pm.exp_year
      ? `${String(pm.exp_month).padStart(2, '0')}/${String(pm.exp_year).slice(-2)}`
      : null;

  return (
    <div
      data-testid='payment-method-row'
      data-pm-id={pm.id}
      data-is-default={pm.is_default ? 'true' : 'false'}
      data-last4={pm.last4 ?? ''}
      className='flex items-center justify-between rounded-lg border border-border p-4'
    >
      <div className='flex min-w-0 items-center gap-3'>
        <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-muted'>
          <CreditCard className='h-5 w-5 text-muted-foreground' />
        </div>
        <div className='min-w-0'>
          <p className='flex items-center gap-2 text-sm font-medium text-foreground'>
            {brandLabel} •••• {pm.last4 ?? '••••'}
            {pm.is_default && (
              <StatusBadge
                data-testid='default-payment-method-badge'
                variant='success'
                className='gap-1'
              >
                <Star className='h-3 w-3' />
                Default
              </StatusBadge>
            )}
          </p>
          {expiry && (
            <p className='text-xs text-muted-foreground'>Expires {expiry}</p>
          )}
        </div>
      </div>
      <div className='flex items-center gap-2'>
        {!pm.is_default && (
          <Button
            data-testid='set-default-button'
            variant='ghost'
            size='sm'
            className='max-sm:h-11'
            onClick={onSetDefault}
            disabled={settingDefault}
          >
            {settingDefault ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : (
              'Set default'
            )}
          </Button>
        )}
        <Button
          data-testid='delete-payment-method-button'
          variant='ghost'
          size='sm'
          onClick={onDelete}
          disabled={deleting}
          // Icon-only, so it had NO accessible name at all — axe
          // `button-name`. A screen-reader user met an unlabelled button next
          // to their saved card and could only find out what it did by
          // pressing it. The brand and last4 are in the name so two cards are
          // distinguishable.
          aria-label={`Remove ${brandLabel} ending ${pm.last4 ?? ''}`.trim()}
          className='text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 max-sm:h-11 max-sm:w-11'
        >
          {deleting ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
          ) : (
            <Trash2 className='h-3.5 w-3.5' />
          )}
        </Button>
      </div>
    </div>
  );
}
